import {
  useAddContractAddendum,
  useAddContractContact,
  useAddContractInvoice,
  useDeleteContract,
  useDeleteContractAddendum,
  useGetContract,
  useRelinkContractEnrollments,
  useSyncClientInvoice,
  useSyncContract,
  useUpdateContract,
} from '@actions';
import Button from '@components/V4/Button';
import Input from '@components/V4/Inputs/Input';
import Textarea from '@components/V4/Inputs/Textarea';
import Modal from '@components/V4/Modal/Modal';
import Select from '@components/V4/Select/Select';
import Tabs from '@components/V4/Tabs/Tabs';
import { ContractRowDTO } from '@shared/clientTypes/DTOs/ContractsDTO';
import {
  CLIENT_INVOICE_STATUS_LABELS,
  CONTRACT_CONTACT_FORM_LABELS,
  CONTRACT_CONTACT_FORM_OPTIONS,
  CONTRACT_STATUS_LABELS,
  CONTRACT_STATUS_OPTIONS,
  ENROLLMENT_STATUS_TYPES,
} from '@shared/constants';
import { ContractContactForm, ContractStatus } from '@shared/enums';
import { Popconfirm, Spin } from 'antd';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { ReactNode, useEffect, useState } from 'react';
import {
  churnTone,
  contractStatusTone,
  formatDate,
  formatMoney,
  frequencyLabel,
  invoiceStatusTone,
} from '../utils';

type DetailsTab = 'overview' | 'invoices' | 'sessions' | 'cs';

interface ContractDetailsModalProps {
  contractId: string;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (contract: ContractRowDTO) => void;
  onOpenStudent: (studentId: string) => void;
}

const INPUT_DATE = 'YYYY-MM-DD';

const Field = (props: { label: string; children: ReactNode; className?: string }) => (
  <div className={mergeClassNames('flex flex-col gap-0.5', props.className)}>
    <span className="text-xs uppercase tracking-wide text-customGrey">{props.label}</span>
    <span className="text-sm text-white">{props.children}</span>
  </div>
);

const SectionTitle = (props: { children: ReactNode }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wide text-customGrey">
    {props.children}
  </h3>
);

const ExternalLink = (props: { href?: string; label: string }) =>
  props.href ? (
    <a
      href={props.href}
      target="_blank"
      rel="noreferrer"
      className="text-customPrimary underline-offset-2 hover:underline"
    >
      {props.label}
    </a>
  ) : (
    <span className="text-customGrey">—</span>
  );

const ContractDetailsModal = (props: ContractDetailsModalProps) => {
  const { contractId, isOpen, onClose, onEdit, onOpenStudent } = props;
  const toast = useToast();

  const { data: contract, isLoading } = useGetContract(contractId, {
    enabled: isOpen && !!contractId,
  });
  const [tab, setTab] = useState<DetailsTab>('overview');

  const onError = (error: any) =>
    toast.error(error?.response?.data?.error?.message ?? 'Something went wrong. Please try again.');

  const { mutate: updateContract, isLoading: isSaving } = useUpdateContract(contractId, {
    onSuccess: () => toast.success('Contract updated.'),
    onError,
  });
  const { mutate: deleteContract, isLoading: isDeleting } = useDeleteContract(contractId, {
    onSuccess: () => {
      toast.success('Contract deleted.');
      onClose();
    },
    onError,
  });
  const { mutate: addContact, isLoading: isAddingContact } = useAddContractContact(contractId, {
    onSuccess: () => toast.success('Contact logged.'),
    onError,
  });
  const { mutate: addAddendum, isLoading: isAddingAddendum } = useAddContractAddendum(contractId, {
    onSuccess: () => toast.success('Addendum added.'),
    onError,
  });
  const { mutate: deleteAddendum } = useDeleteContractAddendum({
    onSuccess: () => toast.success('Addendum removed.'),
    onError,
  });
  const { mutate: relinkEnrollments, isLoading: isRelinking } = useRelinkContractEnrollments(
    contractId,
    { onSuccess: () => toast.success('Enrollments relinked.'), onError }
  );
  const { mutate: addInvoice, isLoading: isAddingInvoice } = useAddContractInvoice(contractId, {
    onSuccess: () => toast.success('Invoice linked.'),
    onError,
  });
  const { mutate: syncInvoice, isLoading: isSyncingInvoice } = useSyncClientInvoice({
    onSuccess: () => toast.success('Invoice refreshed from SmartBill.'),
    onError,
  });

  const { mutate: syncContract, isLoading: isSyncingContract } = useSyncContract({
    onSuccess: () => toast.success('Contract refreshed from SmartBill.'),
    onError,
  });

  const [caseContext, setCaseContext] = useState('');
  const [actions, setActions] = useState('');
  const [retentionSolution, setRetentionSolution] = useState('');
  const [sessionsNotes, setSessionsNotes] = useState('');
  const [finalStatusOverride, setFinalStatusOverride] = useState<ContractStatus | null>(null);
  const [promiseAmount, setPromiseAmount] = useState('');
  const [promiseDate, setPromiseDate] = useState('');
  const [promiseNote, setPromiseNote] = useState('');

  const [contactDate, setContactDate] = useState(moment().format(INPUT_DATE));
  const [contactForm, setContactForm] = useState<ContractContactForm>(ContractContactForm.Call);
  const [contactNote, setContactNote] = useState('');

  const [addendumDate, setAddendumDate] = useState(moment().format(INPUT_DATE));
  const [addendumSessions, setAddendumSessions] = useState('');
  const [addendumAmount, setAddendumAmount] = useState('');
  const [addendumDescription, setAddendumDescription] = useState('');
  const [addendumUrl, setAddendumUrl] = useState('');

  const [invoiceSeries, setInvoiceSeries] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceInstallment, setInvoiceInstallment] = useState<number | null>(null);

  useEffect(() => {
    if (!contract) return;
    setCaseContext(contract.caseContext ?? '');
    setActions(contract.actions ?? '');
    setRetentionSolution(contract.retentionSolution ?? '');
    setSessionsNotes(contract.sessionsNotes ?? '');
    setFinalStatusOverride(contract.finalStatusOverride ?? null);
    setPromiseAmount(contract.paymentPromise ? String(contract.paymentPromise.amount) : '');
    setPromiseDate(
      contract.paymentPromise ? moment(contract.paymentPromise.date).format(INPUT_DATE) : ''
    );
    setPromiseNote(contract.paymentPromise?.note ?? '');
  }, [contract]);

  useEffect(() => {
    if (isOpen) setTab('overview');
  }, [isOpen, contractId]);

  const saveCsFields = () => {
    updateContract({
      caseContext,
      actions,
      retentionSolution,
      sessionsNotes,
      finalStatusOverride,
      paymentPromise:
        promiseAmount && promiseDate
          ? {
              amount: Number(promiseAmount),
              date: moment(promiseDate).toISOString(),
              note: promiseNote || undefined,
            }
          : null,
    });
  };

  const submitContact = () => {
    if (!contactDate) return;
    addContact(
      {
        date: moment(contactDate).toISOString(),
        form: contactForm,
        note: contactNote || undefined,
      },
      { onSuccess: () => setContactNote('') }
    );
  };

  const submitAddendum = () => {
    addAddendum(
      {
        date: moment(addendumDate).toISOString(),
        sessions: Number(addendumSessions) || 0,
        amount: Number(addendumAmount) || 0,
        description: addendumDescription || undefined,
        url: addendumUrl || undefined,
      },
      {
        onSuccess: () => {
          setAddendumSessions('');
          setAddendumAmount('');
          setAddendumDescription('');
          setAddendumUrl('');
        },
      }
    );
  };

  const submitInvoice = () => {
    if (!invoiceSeries.trim() || !invoiceNumber.trim()) return;
    addInvoice(
      {
        series: invoiceSeries.trim(),
        number: invoiceNumber.trim(),
        installmentIndex: invoiceInstallment,
      },
      {
        onSuccess: () => {
          setInvoiceNumber('');
          setInvoiceInstallment(null);
        },
      }
    );
  };

  const renderOverview = (row: ContractRowDTO) => (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-4 tablet:grid-cols-4">
        <Field label="Client (SmartBill)">
          {row.client ? (
            <span>
              {row.client.name}
              {row.client.vatCode ? (
                <span className="text-customGrey"> · {row.client.vatCode}</span>
              ) : null}
            </span>
          ) : (
            <span className="text-customGold">Not matched yet</span>
          )}
        </Field>
        <Field label="Contract number">{row.contractNumber || '—'}</Field>
        <Field label="Contract date">{formatDate(row.contractDate)}</Field>
        <Field label="Documents">
          <span className="flex gap-3">
            <ExternalLink href={row.contractUrl} label="Contract" />
          </span>
        </Field>
        <Field label="Student">
          <button
            type="button"
            className="text-left hover:text-customPrimary hover:underline"
            onClick={() => onOpenStudent(row.student._id)}
          >
            {row.student.fullName}
          </button>
        </Field>
        <Field label="Parent">
          {row.parent.fullName || '—'}
          <span className="block text-xs text-customGrey">
            {[row.parent.email, row.parent.phone].filter(Boolean).join(' · ') || '—'}
          </span>
        </Field>
        <Field label="Strategist">{row.strategist?.fullName ?? '—'}</Field>
        <Field label="Churn risk">
          <span className={mergeClassNames('capitalize', churnTone(row.churn?.level))}>
            {row.churn ? `${row.churn.level} (${row.churn.score})` : '—'}
          </span>
        </Field>
        <Field label="Strategy meeting">{formatDate(row.strategyMeeting?.scheduledDate)}</Field>
        <Field label="Status">
          <span
            className={mergeClassNames(
              'rounded-full px-2 py-0.5 text-xs font-semibold',
              contractStatusTone(row.status)
            )}
          >
            {CONTRACT_STATUS_LABELS[row.status]}
          </span>
        </Field>
        <Field label="Last contact">
          {row.lastContact
            ? `${formatDate(row.lastContact.date)} · ${
                CONTRACT_CONTACT_FORM_LABELS[row.lastContact.form]
              } · ${row.lastContact.by.fullName ?? ''}`
            : '—'}
        </Field>
        <Field label="Payment promise">
          {row.paymentPromise
            ? `${formatMoney(row.paymentPromise.amount, row.currency)} by ${formatDate(
                row.paymentPromise.date
              )}`
            : '—'}
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-2xl bg-surfaceObject p-4 tablet:grid-cols-4">
        <Field label="Contract amount">{formatMoney(row.summary.totalAmount, row.currency)}</Field>
        <Field label="Price / session">
          {formatMoney(row.summary.pricePerSession, row.currency)}
        </Field>
        <Field label="Installments">
          {row.installments.length} · {frequencyLabel(row)}
        </Field>
        <Field label="Amount per installment">
          {[...new Set(row.installments.map((installment) => installment.amount))]
            .map((amount) => formatMoney(amount, row.currency))
            .join(' / ')}
        </Field>
        <Field label="Installments paid">
          {row.summary.installmentsPaid} paid · {row.summary.installmentsRemaining} remaining
        </Field>
        <Field label="Paid so far">
          <span className="text-customGreen">
            {formatMoney(row.summary.paidAmount, row.currency)}
          </span>
        </Field>
        <Field label="Remaining to be paid">
          <span className={row.summary.remainingAmount > 0 ? 'text-customGold' : ''}>
            {formatMoney(row.summary.remainingAmount, row.currency)}
          </span>
        </Field>
        <Field label="Overdue">
          {row.summary.overdueDays > 0 ? (
            <span className="text-customRed">
              {row.summary.overdueDays} days · next due {formatDate(row.summary.nextDueDate)}
            </span>
          ) : row.summary.nextDueDate ? (
            `Next due ${formatDate(row.summary.nextDueDate)}`
          ) : (
            '—'
          )}
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-2xl bg-surfaceObject p-4 tablet:grid-cols-4">
        <Field label="Sessions in contract">{row.summary.sessionsInContract}</Field>
        <Field label="Sessions in Agora">{row.sessions.inAgora}</Field>
        <Field label="Sessions done">
          {row.sessions.done} · {formatMoney(row.sessions.valueDone, row.currency)}
        </Field>
        <Field label="Paid but not done">
          {row.sessions.paidNotDone} · {formatMoney(row.sessions.valuePaidNotDone, row.currency)}
        </Field>
        {row.sessions.autoNotes.length ? (
          <div className="col-span-2 tablet:col-span-4">
            <ul className="list-disc pl-4 text-xs text-customGold">
              {row.sessions.autoNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        <SectionTitle>Addenda (AD)</SectionTitle>
        {row.addenda.length ? (
          <ul className="flex flex-col gap-2">
            {row.addenda.map((addendum) => (
              <li
                key={addendum._id}
                className="flex flex-wrap items-center gap-3 rounded-xl bg-surfaceObject px-3 py-2 text-sm"
              >
                <span className="text-customGrey">{formatDate(addendum.date)}</span>
                <span>+{addendum.sessions} sessions</span>
                <span>{formatMoney(addendum.amount, row.currency)}</span>
                {addendum.description ? (
                  <span className="text-customGrey">{addendum.description}</span>
                ) : null}
                <ExternalLink href={addendum.url} label="Document" />
                <Popconfirm
                  zIndex={99999}
                  title="Remove this addendum?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => deleteAddendum([contractId, addendum._id], undefined)}
                >
                  <button type="button" className="ml-auto text-xs text-customRed hover:underline">
                    Remove
                  </button>
                </Popconfirm>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-sm text-customGrey">No addenda.</span>
        )}
        <div className="grid grid-cols-2 items-end gap-3 tablet:grid-cols-6">
          <Input
            type="date"
            name="addendumDate"
            id="addendumDate"
            label="Date"
            value={addendumDate}
            onChange={(e) => setAddendumDate(e.target.value)}
          />
          <Input
            type="number"
            name="addendumSessions"
            id="addendumSessions"
            label="Extra sessions"
            value={addendumSessions}
            onChange={(e) => setAddendumSessions(e.target.value)}
          />
          <Input
            type="number"
            name="addendumAmount"
            id="addendumAmount"
            label={`Amount (${row.currency})`}
            value={addendumAmount}
            onChange={(e) => setAddendumAmount(e.target.value)}
          />
          <Input
            type="text"
            name="addendumDescription"
            id="addendumDescription"
            label="Description"
            value={addendumDescription}
            onChange={(e) => setAddendumDescription(e.target.value)}
          />
          <Input
            type="text"
            name="addendumUrl"
            id="addendumUrl"
            label="Document link"
            value={addendumUrl}
            onChange={(e) => setAddendumUrl(e.target.value)}
          />
          <Button
            variant="secondary"
            buttonText="Add addendum"
            isLoading={isAddingAddendum}
            disabled={!addendumDate || (!addendumSessions && !addendumAmount)}
            onClick={submitAddendum}
          />
        </div>
      </div>
    </div>
  );

  const renderInvoices = (row: ContractRowDTO) => (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <SectionTitle>Installment plan</SectionTitle>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-customGrey">
              <th className="py-2 pr-3">#</th>
              <th className="py-2 pr-3">Amount</th>
              <th className="py-2 pr-3">Due date</th>
              <th className="py-2 pr-3">Invoice</th>
              <th className="py-2 pr-3">Invoice status</th>
              <th className="py-2">Covered</th>
            </tr>
          </thead>
          <tbody>
            {row.installments.map((installment) => (
              <tr key={installment.index} className="border-t border-white/5">
                <td className="py-2 pr-3">{installment.index}</td>
                <td className="py-2 pr-3">{formatMoney(installment.amount, row.currency)}</td>
                <td className="py-2 pr-3">{formatDate(installment.dueDate)}</td>
                <td className="py-2 pr-3 font-mono">
                  {installment.invoice
                    ? `${installment.invoice.series} ${installment.invoice.number}`
                    : '—'}
                </td>
                <td className="py-2 pr-3">
                  {installment.invoice ? (
                    <span
                      className={mergeClassNames(
                        'rounded-full px-2 py-0.5 text-xs font-semibold',
                        invoiceStatusTone(installment.invoice.status)
                      )}
                    >
                      {CLIENT_INVOICE_STATUS_LABELS[installment.invoice.status]}
                    </span>
                  ) : (
                    <span className="text-customGrey">no invoice</span>
                  )}
                </td>
                <td className="py-2">
                  {installment.paid ? (
                    <span className="text-customGreen">paid</span>
                  ) : (
                    <span className="text-customGrey">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-2">
        <SectionTitle>Invoices (from SmartBill)</SectionTitle>
        {row.invoices.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-customGrey">
                <th className="py-2 pr-3">Invoice</th>
                <th className="py-2 pr-3">Issued</th>
                <th className="py-2 pr-3">Due</th>
                <th className="py-2 pr-3">Total</th>
                <th className="py-2 pr-3">Unpaid</th>
                <th className="py-2 pr-3">Overdue</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2">Synced</th>
              </tr>
            </thead>
            <tbody>
              {row.invoices.map((invoice) => (
                <tr key={invoice._id} className="border-t border-white/5">
                  <td className="py-2 pr-3 font-mono">
                    {invoice.series} {invoice.number}
                    {invoice.installmentIndex ? (
                      <span className="text-customGrey"> · #{invoice.installmentIndex}</span>
                    ) : null}
                    {invoice.autoLinked ? <span className="text-customGrey"> · auto</span> : null}
                  </td>
                  <td className="py-2 pr-3">{formatDate(invoice.issueDate)}</td>
                  <td className="py-2 pr-3">{formatDate(invoice.dueDate)}</td>
                  <td className="py-2 pr-3">
                    {formatMoney(invoice.totalAmount, invoice.currency)}
                  </td>
                  <td className="py-2 pr-3">
                    {formatMoney(invoice.unpaidAmount, invoice.currency)}
                  </td>
                  <td className="py-2 pr-3">
                    {invoice.overdueDays > 0 ? (
                      <span className="text-customRed">{invoice.overdueDays} days</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <span
                      className={mergeClassNames(
                        'rounded-full px-2 py-0.5 text-xs font-semibold',
                        invoiceStatusTone(invoice.status)
                      )}
                    >
                      {CLIENT_INVOICE_STATUS_LABELS[invoice.status]}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-customGrey">
                        {invoice.lastSyncedAt ? moment(invoice.lastSyncedAt).fromNow() : 'never'}
                      </span>
                      <button
                        type="button"
                        disabled={isSyncingInvoice}
                        className="text-xs text-customPrimary hover:underline"
                        onClick={() => syncInvoice([invoice._id], undefined)}
                      >
                        sync
                      </button>
                    </div>
                    {invoice.syncError ? (
                      <span className="block text-xs text-customRed">{invoice.syncError}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <span className="text-sm text-customGrey">
            No invoice linked yet. Invoices discovered in SmartBill are matched automatically by
            amount and due date; you can also link one by series and number below.
          </span>
        )}
        <div className="grid grid-cols-2 items-end gap-3 tablet:grid-cols-4">
          <Input
            type="text"
            name="invoiceSeries"
            id="invoiceSeries"
            label="Series"
            value={invoiceSeries}
            onChange={(e) => setInvoiceSeries(e.target.value)}
          />
          <Input
            type="text"
            name="invoiceNumber"
            id="invoiceNumber"
            label="Number"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
          />
          <Select
            label="Installment"
            variant="primary"
            value={invoiceInstallment}
            options={[
              { label: 'Not linked to an installment', value: null },
              ...row.installments.map((installment) => ({
                label: `#${installment.index} · ${formatMoney(
                  installment.amount,
                  row.currency
                )} · ${formatDate(installment.dueDate)}`,
                value: installment.index,
              })),
            ]}
            onSelect={(value) => setInvoiceInstallment(value === null ? null : Number(value))}
          />
          <Button
            variant="secondary"
            buttonText="Link invoice"
            isLoading={isAddingInvoice}
            disabled={!invoiceSeries.trim() || !invoiceNumber.trim()}
            onClick={submitInvoice}
          />
        </div>
      </div>
    </div>
  );

  const renderSessions = (row: ContractRowDTO) => (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <SectionTitle>Linked enrollments</SectionTitle>
        <Button
          variant="secondary"
          buttonText="Relink active enrollments"
          isLoading={isRelinking}
          onClick={() => relinkEnrollments(undefined)}
        />
      </div>
      {row.enrollments.length ? (
        <ul className="flex flex-col gap-2">
          {row.enrollments.map((enrollment) => (
            <li
              key={enrollment._id}
              className="flex items-center gap-3 rounded-xl bg-surfaceObject px-3 py-2 text-sm"
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: enrollment.courseColor }}
              />
              <span>{enrollment.courseTitle}</span>
              <span className="text-customGrey">
                {enrollment.status !== undefined ? ENROLLMENT_STATUS_TYPES[enrollment.status] : ''}
              </span>
              <span className="ml-auto">
                {enrollment.sessionsDone} / {enrollment.sessionsCount} sessions done
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <span className="text-sm text-customGrey">No enrollment linked.</span>
      )}
      {row.sessions.autoNotes.length ? (
        <ul className="list-disc pl-4 text-sm text-customGold">
          {row.sessions.autoNotes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
      <Textarea
        name="sessionsNotes"
        id="sessionsNotes"
        label="Observations about sessions"
        rows={3}
        value={sessionsNotes}
        onChange={(e) => setSessionsNotes(e.target.value)}
      />
      <div className="flex justify-end">
        <Button variant="primary" buttonText="Save" isLoading={isSaving} onClick={saveCsFields} />
      </div>
    </div>
  );

  const renderCs = (row: ContractRowDTO) => (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
        <Textarea
          name="caseContext"
          id="caseContext"
          label="Case context"
          rows={3}
          value={caseContext}
          onChange={(e) => setCaseContext(e.target.value)}
        />
        <Textarea
          name="actions"
          id="actions"
          label="Actions"
          rows={3}
          value={actions}
          onChange={(e) => setActions(e.target.value)}
        />
        <Textarea
          name="retentionSolution"
          id="retentionSolution"
          label="Possible retention solution"
          rows={3}
          value={retentionSolution}
          onChange={(e) => setRetentionSolution(e.target.value)}
        />
        <div className="flex flex-col gap-3">
          <Select
            label="Final status override"
            variant="primary"
            value={finalStatusOverride}
            options={[{ label: 'Computed automatically', value: null }, ...CONTRACT_STATUS_OPTIONS]}
            onSelect={(value) => setFinalStatusOverride((value as ContractStatus) || null)}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              name="promiseAmount"
              id="promiseAmount"
              label={`Payment promise (${row.currency})`}
              value={promiseAmount}
              onChange={(e) => setPromiseAmount(e.target.value)}
            />
            <Input
              type="date"
              name="promiseDate"
              id="promiseDate"
              label="Promised by"
              value={promiseDate}
              onChange={(e) => setPromiseDate(e.target.value)}
            />
          </div>
          <Input
            type="text"
            name="promiseNote"
            id="promiseNote"
            label="Promise note"
            value={promiseNote}
            onChange={(e) => setPromiseNote(e.target.value)}
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button variant="primary" buttonText="Save" isLoading={isSaving} onClick={saveCsFields} />
      </div>

      <div className="flex flex-col gap-3">
        <SectionTitle>Contact log</SectionTitle>
        <div className="grid grid-cols-2 items-end gap-3 tablet:grid-cols-5">
          <Input
            type="date"
            name="contactDate"
            id="contactDate"
            label="Date"
            value={contactDate}
            onChange={(e) => setContactDate(e.target.value)}
          />
          <Select
            label="Form"
            variant="primary"
            value={contactForm}
            options={CONTRACT_CONTACT_FORM_OPTIONS}
            onSelect={(value) => setContactForm(value as ContractContactForm)}
          />
          <Input
            type="text"
            name="contactNote"
            id="contactNote"
            label="Note"
            value={contactNote}
            onChange={(e) => setContactNote(e.target.value)}
            className="tablet:col-span-2"
          />
          <Button
            variant="secondary"
            buttonText="Log contact"
            isLoading={isAddingContact}
            onClick={submitContact}
          />
        </div>
        {row.contactLog.length ? (
          <ul className="flex flex-col gap-2">
            {row.contactLog.map((contact) => (
              <li
                key={contact._id}
                className="flex flex-wrap gap-3 rounded-xl bg-surfaceObject px-3 py-2 text-sm"
              >
                <span className="text-customGrey">{formatDate(contact.date)}</span>
                <span>{CONTRACT_CONTACT_FORM_LABELS[contact.form]}</span>
                <span className="text-customGrey">{contact.by.fullName}</span>
                {contact.note ? (
                  <span className="w-full text-customLight">{contact.note}</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-sm text-customGrey">No contact logged yet.</span>
        )}
      </div>
    </div>
  );

  const renderContent = (currentTab: DetailsTab) => {
    if (!contract) return null;
    switch (currentTab) {
      case 'overview':
        return renderOverview(contract);
      case 'invoices':
        return renderInvoices(contract);
      case 'sessions':
        return renderSessions(contract);
      case 'cs':
        return renderCs(contract);
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="large">
      <Modal.Header
        title={
          contract
            ? `${contract.student.fullName} · ${contract.contractNumber || 'Contract'}`
            : 'Contract'
        }
      />
      <Modal.Body className="flex flex-col gap-4">
        {isLoading || !contract ? (
          <div className="flex justify-center py-16">
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={mergeClassNames(
                  'rounded-full px-2 py-0.5 text-xs font-semibold',
                  contractStatusTone(contract.status)
                )}
              >
                {CONTRACT_STATUS_LABELS[contract.status]}
              </span>
              <span className="text-xs text-customGrey">
                Updated {contract.updatedAt ? moment(contract.updatedAt).fromNow() : '—'} · summary
                computed {moment(contract.summary.computedAt).fromNow()}
              </span>
              <div className="ml-auto flex gap-2">
                <Button
                  variant="secondary"
                  buttonText="Sync SmartBill"
                  isLoading={isSyncingContract}
                  onClick={() => syncContract([contractId], undefined)}
                />
                <Button
                  variant="secondary"
                  buttonText="Edit contract"
                  onClick={() => onEdit(contract)}
                />
                <Popconfirm
                  zIndex={99999}
                  title="Delete this contract? Linked invoices go back to the unmapped queue."
                  okText="Delete"
                  cancelText="Cancel"
                  onConfirm={() => deleteContract(undefined)}
                >
                  <Button variant="secondary-red" buttonText="Delete" isLoading={isDeleting} />
                </Popconfirm>
              </div>
            </div>
            <Tabs
              tabs={[
                { label: 'Overview', value: 'overview' },
                { label: `Invoices (${contract.invoices.length})`, value: 'invoices' },
                { label: 'Sessions', value: 'sessions' },
                { label: 'CS & contacts', value: 'cs' },
              ]}
              activeTab={tab}
              onTabChange={setTab}
              renderContent={renderContent}
            />
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ContractDetailsModal;
